import { FormEvent, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearAuthError, loginRequest } from '../../features/auth/authSlice';

const FIELD: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1.5px solid #f9c0d8',
  fontSize: 14,
  background: '#fff5f8',
  color: '#6b0030',
  outline: 'none',
  boxSizing: 'border-box',
};

const BTN: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '11px 0',
  borderRadius: 12,
  fontWeight: 700,
  fontSize: 14,
  background: '#ffffff',
  color: '#d6006e',
  border: '2px solid #d6006e',
  boxShadow: '0 4px 16px rgba(212,0,106,0.14)',
  cursor: 'pointer',
};

function StarField() {
  const stars = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        id: i,
        left: `${(i * 41) % 94}%`,
        top: `${(i * 47) % 90}%`,
        size: 2 + (i % 3),
        delay: `${(i % 6) * 0.4}s`,
      })),
    [],
  );

  return (
    <>
      {stars.map((s) => (
        <span
          key={s.id}
          className="pointer-events-none absolute rounded-full bg-white"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            opacity: 0.3,
            animation: `mz-star 3.4s ease-in-out ${s.delay} infinite`,
          }}
        />
      ))}
    </>
  );
}

function WaveEdge() {
  return (
    <svg
      className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[42%] md:block lg:w-[38%]"
      viewBox="0 0 420 900"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M155 0 C100 110, 230 180, 165 280 C95 400, 235 470, 170 580 C105 700, 215 780, 145 900 L420 900 L420 0 Z"
        fill="#ffffff"
      />
    </svg>
  );
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, token } = useAppSelector((s) => s.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (token) navigate('/', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    dispatch(loginRequest({ email: email.trim(), password }));
  };

  return (
    <div className="relative flex h-screen max-h-screen flex-col overflow-hidden bg-[#1a000f]">
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 75% 15%, rgba(255,79,162,0.4), transparent 42%), radial-gradient(ellipse at 20% 70%, rgba(122,0,56,0.7), transparent 50%), linear-gradient(160deg, #12000c 0%, #5e002f 40%, #c4005e 72%, #ff4fa2 100%)',
        }}
      />
      <StarField />
      <div className="mz-scan pointer-events-none absolute inset-x-0 h-28 bg-gradient-to-b from-white/15 to-transparent" />
      <div
        className="mz-orb pointer-events-none absolute right-[30%] top-[20%] h-56 w-56 rounded-full blur-3xl"
        style={{ background: 'rgba(255,100,170,0.4)' }}
      />
      <WaveEdge />

      <div className="relative z-20 flex shrink-0 items-center gap-3 px-6 pt-5 sm:px-10 lg:px-14 mz-fade-up">
        <BrandLogo size={34} className="brightness-0 invert" />
        <div>
          <p className="text-lg font-black tracking-tight text-white">Micus</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/70">
            Admin Console
          </p>
        </div>
        <span
          className="ml-2 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)',
          }}
        >
          Restricted
        </span>
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 items-center gap-6 overflow-hidden px-6 py-4 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:px-14 lg:py-2">
        <div className="mz-fade-up mz-d1 hidden max-w-xl text-white lg:block">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-white/70">
            Control plane
          </p>
          <h1 className="text-3xl font-black leading-[1.1] tracking-tight xl:text-4xl">
            Operate users, companies & ratings
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">
            Manage access, pricing, country document checklists and the AI
            rating engine from one console.
          </p>

          <div className="mt-5 grid max-w-md grid-cols-2 gap-2.5">
            {[
              { t: 'Users', d: 'Accounts & access' },
              { t: 'Fees', d: 'Pricing per country' },
              { t: 'Documents', d: 'Country checklists' },
              { t: 'Ratings', d: 'Versioned prompts' },
            ].map((cell) => (
              <div
                key={cell.t}
                className="rounded-xl px-3 py-2.5"
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <p className="text-sm font-bold text-white">{cell.t}</p>
                <p className="text-[11px] text-white/65">{cell.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-20 mx-auto w-full max-w-[380px] mz-fade-up mz-d2">
          <div
            className="rounded-2xl p-6 sm:p-7"
            style={{
              background: 'rgba(255,255,255,0.95)',
              boxShadow: '0 24px 60px rgba(212,0,106,0.24), 0 8px 20px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.85)',
            }}
          >
            <div className="mb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-[#d6006e]">
                Admin access
              </span>
              <h2 className="mt-1 text-xl font-black tracking-tight text-[#6b0030]">
                Sign in to console
              </h2>
              <p className="mt-0.5 text-sm text-[#c084a0]">
                Restricted to admin-role operators only.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-3 rounded-xl px-3.5 py-2.5 text-sm"
                style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c' }}
              >
                {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-3.5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#9d0050]">
                  Admin email
                </label>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="one@lab63.org"
                  className="mz-field"
                  style={FIELD}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#9d0050]">Password</label>
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-xs font-semibold text-[#d6006e]"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mz-field"
                  style={FIELD}
                />
              </div>

              <button type="submit" disabled={loading} className="mz-cta" style={BTN}>
                {loading ? 'Authenticating…' : 'Enter console →'}
              </button>
            </form>

            <p className="mt-4 text-center text-xs text-[#c084a0]">
              Customer workspace →{' '}
              <span className="font-semibold text-[#d6006e]">app.micusify.com</span>
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-20 flex shrink-0 flex-wrap justify-center gap-2 px-6 pb-4 pt-1 mz-fade-up mz-d3">
        {['Users', 'Companies', 'Documents', 'Ratings'].map((label) => (
          <span
            key={label}
            className="rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1a0010]"
            style={{
              background: 'rgba(255,255,255,0.55)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.35)',
            }}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
